import React, { useState } from 'react';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { THEMES, useAppTheme } from '../../theme/AppThemeContext';

type AppLanguage = 'al' | 'en' | 'it';

const APP_LANGUAGES: Array<{ value: AppLanguage; country: string; label: string }> = [
  { value: 'al', country: 'AL', label: 'Shqip' },
  { value: 'en', country: 'GB', label: 'English' },
  { value: 'it', country: 'IT', label: 'Italiano' },
];

type Props = {
  variant: 'landing' | 'sidebar-theme' | 'sidebar-language';
  sidebarLanguagePlacement?: 'top' | 'bottom';
};

const AppPreferenceControls: React.FC<Props> = ({ variant, sidebarLanguagePlacement = 'top' }) => {
  const { i18n } = useTranslation();
  const { theme, setTheme, language, setLanguage } = useAppTheme();
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const activeLanguageCode = (language || i18n.language || 'al').split('-')[0] as AppLanguage;
  const activeLanguage = APP_LANGUAGES.find((item) => item.value === activeLanguageCode) ?? APP_LANGUAGES[0];

  const chooseLanguage = (code: AppLanguage) => {
    setLanguage(code);
    setIsLanguageOpen(false);
  };

  if (variant === 'sidebar-theme') {
    return (
      <div className="lsThemeMenu">
        <button
          type="button"
          className={`lsThemeMenuHeader ${isThemeOpen ? 'isOpen' : ''}`}
          onClick={() => setIsThemeOpen((v) => !v)}
          aria-expanded={isThemeOpen}
        >
          <span className="lsThemeMenuHeaderLeft">
            <PaletteOutlinedIcon fontSize="small" />
            <span>{i18n.t('generic.themes')}</span>
          </span>
          <span className="lsThemeMenuChevron">▾</span>
        </button>

        <div className={`lsThemeMenuBody ${isThemeOpen ? 'isOpen' : ''}`}>
          <div className="lsThemeMenuList">
            {THEMES.map((themeOption) => (
              <button
                key={themeOption}
                type="button"
                className={`lsThemeMenuItem ${theme === themeOption ? 'isActive' : ''}`}
                onClick={() => setTheme(themeOption)}
                title={themeOption}
                aria-label={`Use ${themeOption} theme`}
              >
                <span className={`lsThemeDot dot-${themeOption}`} />
                <span className="lsThemeMenuText">{themeOption}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'sidebar-language') {
    const isBottom = sidebarLanguagePlacement === 'bottom';

    return (
      <div className={`lsLangWrap ${isBottom ? 'lsLangWrapBottom' : ''}`}>
        <button type="button" className="lsLangBtn" onClick={() => setIsLanguageOpen((v) => !v)}>
          <ReactCountryFlag svg countryCode={activeLanguage.country} className="lsLangSvg" />
          <span className="lsLangCode">{activeLanguage.value.toUpperCase()}</span>
          <span className="lsLangChevron">▼</span>
        </button>

        {isLanguageOpen ? (
          <div className={`lsLangMenu ${isBottom ? 'lsLangMenuBottom' : ''}`}>
            {APP_LANGUAGES.map((languageOption) => (
              <div
                key={languageOption.value}
                onClick={() => chooseLanguage(languageOption.value)}
                className={`lsLangItem ${languageOption.value === activeLanguageCode ? 'isActive' : ''}`}
              >
                <ReactCountryFlag svg countryCode={languageOption.country} className="lsLangSvg" />
                <span className="lsLangItemLabel">{languageOption.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="theme-dropdown">
        <button
          className="theme-dropdown-trigger"
          onClick={() => {
            setIsThemeOpen((prev) => !prev);
            setIsLanguageOpen(false);
          }}
          type="button"
        >
          <PaletteOutlinedIcon fontSize="small" />
          <span>{i18n.t('mainLanding.theme.label', { defaultValue: 'Theme' })}: {theme}</span>
        </button>

        {isThemeOpen ? (
          <div className="theme-dropdown-menu">
            {THEMES.map((themeOption) => (
              <button
                key={themeOption}
                className={`theme-dropdown-item ${theme === themeOption ? 'active' : ''}`}
                onClick={() => {
                  setTheme(themeOption);
                  setIsThemeOpen(false);
                }}
                type="button"
              >
                <span className={`theme-dot theme-dot--${themeOption}`} />
                <span>{themeOption}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="theme-dropdown">
        <button
          className="theme-dropdown-trigger theme-dropdown-trigger--lang"
          onClick={() => {
            setIsLanguageOpen((prev) => !prev);
            setIsThemeOpen(false);
          }}
          type="button"
        >
          <ReactCountryFlag svg countryCode={activeLanguage.country} className="landing-lang-flag" />
          <span className="landing-lang-code">{activeLanguage.value.toUpperCase()}</span>
          <span className="landing-lang-label">{activeLanguage.label}</span>
        </button>

        {isLanguageOpen ? (
          <div className="theme-dropdown-menu landing-language-menu">
            {APP_LANGUAGES.map((languageOption) => (
              <button
                key={languageOption.value}
                className={`theme-dropdown-item landing-language-item ${activeLanguageCode === languageOption.value ? 'active' : ''}`}
                onClick={() => chooseLanguage(languageOption.value)}
                type="button"
              >
                <ReactCountryFlag svg countryCode={languageOption.country} className="landing-lang-flag" />
                <span className="landing-lang-item-label">{languageOption.label}</span>
                <span className="landing-lang-item-code">{languageOption.value.toUpperCase()}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default AppPreferenceControls;
